<template>
  <div class="policies">
    <div class="table">
      <div class="table__header box box--default">
        <div class="table__label table__label--medium">{{ $t(`mandatesTable.${"type"}`) }}</div>
        <div class="table__label table__label--medium">{{ $t(`mandatesTable.${"agent"}`) }}</div>
        <div class="table__label table__label--medium">{{ $t(`mandatesTable.${"noticeAgent"}`) }}</div>
        <div class="table__label table__label--medium">{{ $t(`mandatesTable.${"startedDate"}`) }}</div>
        <div class="table__label table__label--medium">{{ $t(`mandatesTable.${"signedDate"}`) }}</div>
        <div class="table__label table__label--large table__label--more-horizontal table__label--last"></div>
      </div>
      <Loading v-if="mandatesLoading" />
      <div v-else-if="!canAccessMandates" class="table__body">
        <div class="table__no-data">
          <NoData :type="'mandates'" />
        </div>
      </div>
      <div v-else class="table__body">
        <div v-if="!mandates.length" class="table__no-data">
          <NoData :type="'mandates'" />
        </div>
        <div v-else class="table__policies">
          <div v-if="isMobileView.isMobileView">
            <div v-for="mandate in mandates" :key="mandate.id" class="data-record">
              <div class="data-card box box--default policy">
                <header class="data-card__header">
                  <div class="data-card__identity"><span class="data-card__name">{{ mandate.mandate_type ?
                    mandate.mandate_type.name : '-----' }}</span></div>
                  <div class="actions-wrapper row__data">
                    <div
                      v-if="hasRequiredSignatures(mandate) && mandate.customer_mandate_mode?.id === 1 && canEditMandate"
                      @click="downloadMandate(mandate.id)" class="row__data row__data--small row__data--download"
                      :class="{ 'icon--downloading': downloadingId === mandate.id }"
                      :title="downloadingId === mandate.id ? 'Downloading...' : 'Download Mandate'"
                      :style="{ cursor: 'pointer' }"></div>

                    <div v-else-if="hasRequiredSignatures(mandate) && isManualMandate(mandate) && canEditMandate"
                      class="row__data row__data--small row__data--pdf icon icon__pdf"
                      :class="{ 'icon__pdf--active': mandate.files?.length }" @click="openManualMandateFiles(mandate)"
                      :style="{ cursor: 'pointer' }">
                    </div>

                    <div v-if="mandate.mandate_type?.id !== 13 && hasRequiredSignatures(mandate) && canEditMandate"
                      @click="openMandateEmailModal(mandate.id)" class="row__data row__data--small row__data--email"
                      :class="{
                        'email--draft': hasDraftDeliveries(mandate),
                        'email--sent': hasCompanyDeliveries(mandate) && !hasDraftDeliveries(mandate)
                      }" :title="$t('mandateEmailModal.title')" :style="{ cursor: 'pointer' }">
                    </div>

                    <div v-if="hasRequiredSignatures(mandate) && hasCompanyDeliveries(mandate)"
                      @click="toggleDeliveriesExpand(mandate.id)" class="row__data row__data--small row__data--more"
                      :class="{ 'row__data--rotate': expandedDeliveries[mandate.id] }"></div>
                  </div>



                  <div v-if="!hasRequiredSignatures(mandate) && !mandate.is_signed"
                    class="row__data row__data--small row__data--delete icon__delete"
                    :class="{ 'icon__delete--active': activeMandateId === mandate.id }" tabindex="-1"
                    @focus="activeMandateId = mandate.id" @focusout="activeMandateId = null"
                    :ref="'delete-' + mandate.id">
                    <transition name="fade">
                      <div class="delete" v-if="activeMandateId == mandate.id">
                        <div class="delete__text">
                          {{ $t('messages.deleteMandateConfirmation') }}
                        </div>
                        <div class="delete__buttons">
                          <div class="button button--confirm" @click="deleteMandate(mandate.id)"></div>
                          <div class="button button--cancel" @click="closeMandateDeletePopup(mandate.id)"></div>
                        </div>
                      </div>
                    </transition>
                  </div>
                </header>
                <dl class="data-card__details">
                  <div v-if="mandate.agent" class="data-card__detail">
                    <dt>{{ $t(`mandatesTable.${"agent"}`) }}</dt>
                    <dd>
                      {{ mandate.agent.first_name }} {{ mandate.agent.last_name }}
                      <span v-if="mandate.agent.company_name">
                        ({{ mandate.agent.company_name }})
                      </span>
                    </dd>
                  </div>
                  <div v-if="mandate.notice_agent" class="data-card__detail">
                    <dt>{{ $t(`mandatesTable.${"noticeAgent"}`) }}</dt>
                    <dd> {{ mandate.notice_agent.first_name }} {{ mandate.notice_agent.last_name }}
                      <span v-if="mandate.notice_agent.company_name">
                        ({{ mandate.notice_agent.company_name }})
                      </span>
                    </dd>
                  </div>
                  <div v-if="mandate.created_at" class="data-card__detail">
                    <dt>{{ $t(`mandatesTable.${"startedDate"}`) }}</dt>
                    <dd> {{ formatDate(mandate.created_at) }}</dd>
                  </div>
                  <div v-if="mandate.signed_date" class="data-card__detail">
                    <dt>{{ $t(`mandatesTable.${"signedDate"}`) }}</dt>
                    <dd>{{ formatDate(mandate.signed_date) }}</dd>
                  </div>
                </dl>

                <div v-if="!hasRequiredSignatures(mandate)" class="signing-status-messages">
                  <div v-if="!mandate.is_signed" class="status-message status-message--warning">
                    <span class="status-icon">⚠</span>
                    <span class="status-text">{{ $t('mandatesTable.notSignedByCustomer') }}</span>
                  </div>
                  <div v-if="requiresAgentSignature(mandate) && !mandate.is_signed_by_agent"
                    class="status-message status-message--warning">
                    <span class="status-icon">⚠</span>
                    <span class="status-text">{{ $t('mandatesTable.notSignedByAgent') }}</span>
                  </div>
                </div>

                <!-- Company Deliveries Section -->
                <div v-if="hasRequiredSignatures(mandate) && hasCompanyDeliveries(mandate)" class="policy__services"
                  :class="{ 'policy__services--expand': expandedDeliveries[mandate.id] }">
                  <div v-if="isMobileView.isMobileView" class="services"
                    :class="{ 'services--expand': expandedDeliveries[mandate.id] }">
                    <article v-for="(expandedRow, idx) in mandate.companyDeliveries" :key="`${expandedRow.id}-${idx}`"
                      class="data-card box box--default policy-service"
                      :class="`delivery--status--${expandedRow.status.id}`">
                      <header class="data-card__header">
                        <div class="data-card__identity">
                          <span class="data-card__name">{{ expandedRow.company_name || '-----' }}</span>
                        </div>
                      </header>

                      <dl class="data-card__details">
                        <div class="data-card__detail">
                          <dt>{{ $t('mandatesTable.companyName') }}</dt>
                          <dd>{{ expandedRow.company_name || '-----' }}</dd>
                        </div>
                        <div class="data-card__detail">
                          <dt>{{ $t('mandatesTable.email') }}</dt>
                          <dd>{{ expandedRow.recipient_email || '-----' }}</dd>
                        </div>
                        <div class="data-card__detail">
                          <dt>{{ $t('mandatesTable.policyNumber') }}</dt>
                          <dd>
                            <span v-if="expandedRow.policy_number">
                              {{ expandedRow.policy_number }}
                            </span>
                            <span v-else>-----</span>
                          </dd>
                        </div>
                        <div class="data-card__detail">
                          <dt>{{ $t('mandatesTable.sender') }}</dt>
                          <dd>
                            <template v-if="expandedRow.sender">
                              {{ expandedRow.sender.first_name }} {{ expandedRow.sender.last_name }}
                            </template>
                            <template v-else>-----</template>
                          </dd>
                        </div>
                        <div class="data-card__detail">
                          <dt>{{ $t('mandatesTable.status') }}</dt>
                          <dd>{{ expandedRow.status.name }}</dd>
                        </div>
                        <div class="data-card__detail">
                          <dt>{{ $t('mandatesTable.sentDate') }}</dt>
                          <dd>
                            <template v-if="expandedRow.sent_at">
                              {{ formatDate(expandedRow.sent_at) }}
                            </template>
                            <template v-else>-----</template>
                          </dd>
                        </div>
                      </dl>
                    </article>
                  </div>

                  <div v-else class="services" :ref="`services-${mandate.id}`" :style="expandedDeliveries[mandate.id]
                    ? { maxHeight: `${calculatedDeliveriesHeights[mandate.id]}px` }
                    : { maxHeight: 0 }
                    " :class="{ 'services--expand': expandedDeliveries[mandate.id] }">
                    <div class="table__header table__header--inner">
                      <div class="table__label table__label--medium">{{ $t('mandatesTable.companyName') }}</div>
                      <div class="table__label table__label--medium">{{ $t('mandatesTable.email') }}</div>
                      <div class="table__label table__label--medium">{{ $t('mandatesTable.policyNumber') }}</div>
                      <div class="table__label table__label--medium">{{ $t('mandatesTable.sender') }}</div>
                      <div class="table__label table__label--medium">{{ $t('mandatesTable.status') }}</div>
                      <div class="table__label table__label--medium">{{ $t('mandatesTable.sentDate') }}</div>
                    </div>
                    <div>
                      <div v-for="(expandedRow, idx) in mandate.companyDeliveries" :key="`${expandedRow.id}-${idx}`"
                        class="policy-service" :class="`delivery--status--${expandedRow.status.id}`">
                        <div class="policy-service__row">
                          <div class="policy-service__data delivery-company">{{ expandedRow.company_name }}</div>
                          <div class="policy-service__data">{{ expandedRow.recipient_email }}</div>
                          <div class="policy-service__data">
                            <span v-if="expandedRow.policy_number">
                              {{ expandedRow.policy_number }}
                            </span>
                            <span v-else>-----</span>
                          </div>
                          <div class="policy-service__data">
                            <template v-if="expandedRow.sender">
                              {{ expandedRow.sender.first_name }} {{ expandedRow.sender.last_name }}
                            </template>
                            <template v-else>-----</template>
                          </div>
                          <div class="policy-service__data centered-row row__data row__data--underline"
                            :style="{ cursor: expandedRow.status.code === 'draft' ? 'default' : 'pointer' }"
                            @click="openStatusPopup(expandedRow.id, expandedRow.status)">
                            <transition name="fade" mode="out-in">
                              <PopupEditer class="mandate-status-popup"
                                v-if="showStatusPopup && editingDeliveryId === expandedRow.id"
                                @togglePopup="showStatusPopup = $event" :items="statuses"
                                :selected="expandedRow.status.name" label="name" @submit="updateStatus($event)" />
                            </transition>

                            {{ expandedRow.status.name }}
                          </div>
                          <div class="policy-service__data centered-row">
                            <template v-if="expandedRow.sent_at">
                              {{ formatDate(expandedRow.sent_at) }}
                            </template>
                            <template v-else>-----</template>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-else>
            <div v-for="mandate in mandates" :key="mandate.id" class="policy">
              <div class="row row--policy box box--default">
                <div class="row__data row__data--medium row__data">
                  {{ mandate.mandate_type ? mandate.mandate_type.name : '-----' }}
                </div>
                <div class="row__data row__data--medium row__data">
                  <template v-if="mandate.agent">
                    {{ mandate.agent.first_name }} {{ mandate.agent.last_name }}
                    <span v-if="mandate.agent.company_name">
                      ({{ mandate.agent.company_name }})
                    </span>
                  </template>
                  <template v-else>-----</template>
                </div>
                <div class="row__data row__data--medium row__data">
                  <template v-if="mandate.notice_agent">
                    {{ mandate.notice_agent.first_name }} {{ mandate.notice_agent.last_name }}
                    <span v-if="mandate.notice_agent.company_name">
                      ({{ mandate.notice_agent.company_name }})
                    </span>
                  </template>
                  <template v-else>-----</template>
                </div>
                <div class="row__data row__data--medium row__data mandate-date centered-row">
                  <template v-if="mandate.created_at">
                    {{ formatDate(mandate.created_at) }}
                  </template>
                  <template v-else>-----</template>
                </div>
                <div class="row__data row__data--medium row__data mandate-date centered-row">
                  <template v-if="mandate.signed_date">
                    {{ formatDate(mandate.signed_date) }}
                  </template>
                  <template v-else>-----</template>
                </div>


                <div class="row__data row__data--large row__data--half row__data--last">
                  <div
                    v-if="hasRequiredSignatures(mandate) && mandate.customer_mandate_mode?.id === 1 && canEditMandate"
                    @click="downloadMandate(mandate.id)" class="row__data row__data--small row__data--download"
                    :class="{ 'icon--downloading': downloadingId === mandate.id }"
                    :title="downloadingId === mandate.id ? 'Downloading...' : 'Download Mandate'"
                    :style="{ cursor: 'pointer' }">
                  </div>

                  <div v-else-if="hasRequiredSignatures(mandate) && isManualMandate(mandate) && canEditMandate"
                    class="row__data row__data--small row__data--pdf icon icon__pdf"
                    :class="{ 'icon__pdf--active': mandate.files?.length }" @click="openManualMandateFiles(mandate)"
                    :style="{ cursor: 'pointer' }">
                  </div>

                  <div v-if="mandate.mandate_type?.id !== 13 && hasRequiredSignatures(mandate) && canEditMandate"
                    @click="openMandateEmailModal(mandate.id)" class="row__data row__data--small row__data--email"
                    :class="{
                      'email--draft': hasDraftDeliveries(mandate),
                      'email--sent': hasCompanyDeliveries(mandate) && !hasDraftDeliveries(mandate)
                    }" :title="$t('mandateEmailModal.title')" :style="{ cursor: 'pointer' }">
                  </div>

                  <div v-if="hasRequiredSignatures(mandate) && hasCompanyDeliveries(mandate)"
                    @click="toggleDeliveriesExpand(mandate.id)" class="row__data row__data--small row__data--more"
                    :class="{ 'row__data--rotate': expandedDeliveries[mandate.id] }"></div>
                </div>



                <div v-if="!hasRequiredSignatures(mandate) && !mandate.is_signed"
                  class="row__data row__data--small row__data--delete icon__delete"
                  :class="{ 'icon__delete--active': activeMandateId === mandate.id }" tabindex="-1"
                  @focus="activeMandateId = mandate.id" @focusout="activeMandateId = null"
                  :ref="'delete-' + mandate.id">
                  <transition name="fade">
                    <div class="delete" v-if="activeMandateId == mandate.id">
                      <div class="delete__text">
                        {{ $t('messages.deleteMandateConfirmation') }}
                      </div>
                      <div class="delete__buttons">
                        <div class="button button--confirm" @click="deleteMandate(mandate.id)"></div>
                        <div class="button button--cancel" @click="closeMandateDeletePopup(mandate.id)"></div>
                      </div>
                    </div>
                  </transition>
                </div>
              </div>

              <!-- Signing status messages -->
              <div v-if="!hasRequiredSignatures(mandate)" class="signing-status-messages">
                <div v-if="!mandate.is_signed" class="status-message status-message--warning">
                  <span class="status-icon">⚠</span>
                  <span class="status-text">{{ $t('mandatesTable.notSignedByCustomer') }}</span>
                </div>
                <div v-if="requiresAgentSignature(mandate) && !mandate.is_signed_by_agent"
                  class="status-message status-message--warning">
                  <span class="status-icon">⚠</span>
                  <span class="status-text">{{ $t('mandatesTable.notSignedByAgent') }}</span>
                </div>
              </div>

              <!-- Company Deliveries Section -->
              <div v-if="hasRequiredSignatures(mandate) && hasCompanyDeliveries(mandate)" class="policy__services"
                :class="{ 'policy__services--expand': expandedDeliveries[mandate.id] }">
                <div class="services" :ref="`services-${mandate.id}`" :style="expandedDeliveries[mandate.id]
                  ? { maxHeight: `${calculatedDeliveriesHeights[mandate.id]}px` }
                  : { maxHeight: 0 }
                  " :class="{ 'services--expand': expandedDeliveries[mandate.id] }">
                  <div class="table__header table__header--inner">
                    <div class="table__label table__label--medium">{{ $t('mandatesTable.companyName') }}</div>
                    <div class="table__label table__label--medium">{{ $t('mandatesTable.email') }}</div>
                    <div class="table__label table__label--medium">{{ $t('mandatesTable.policyNumber') }}</div>
                    <div class="table__label table__label--medium">{{ $t('mandatesTable.sender') }}</div>
                    <div class="table__label table__label--medium">{{ $t('mandatesTable.status') }}</div>
                    <div class="table__label table__label--medium">{{ $t('mandatesTable.sentDate') }}</div>
                  </div>
                  <div>
                    <div v-for="(expandedRow, idx) in mandate.companyDeliveries" :key="`${expandedRow.id}-${idx}`"
                      class="policy-service" :class="`delivery--status--${expandedRow.status.id}`">
                      <div class="policy-service__row">
                        <div class="policy-service__data delivery-company">{{ expandedRow.company_name }}</div>
                        <div class="policy-service__data">{{ expandedRow.recipient_email }}</div>
                        <div class="policy-service__data">
                          <span v-if="expandedRow.policy_number">
                            {{ expandedRow.policy_number }}
                          </span>
                          <span v-else>-----</span>
                        </div>
                        <div class="policy-service__data">
                          <template v-if="expandedRow.sender">
                            {{ expandedRow.sender.first_name }} {{ expandedRow.sender.last_name }}
                          </template>
                          <template v-else>-----</template>
                        </div>
                        <div class="policy-service__data centered-row row__data row__data--underline"
                          :style="{ cursor: expandedRow.status.code === 'draft' ? 'default' : 'pointer' }"
                          @click="openStatusPopup(expandedRow.id, expandedRow.status)">
                          <transition name="fade" mode="out-in">
                            <PopupEditer class="mandate-status-popup"
                              v-if="showStatusPopup && editingDeliveryId === expandedRow.id"
                              @togglePopup="showStatusPopup = $event" :items="statuses"
                              :selected="expandedRow.status.name" label="name" @submit="updateStatus($event)" />
                          </transition>

                          {{ expandedRow.status.name }}
                        </div>
                        <div class="policy-service__data centered-row">
                          <template v-if="expandedRow.sent_at">
                            {{ formatDate(expandedRow.sent_at) }}
                          </template>
                          <template v-else>-----</template>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Loading from "@/partials/Loading";
import NoData from "@/partials/NoData";
import { mapGetters } from "vuex";
import PopupEditer from "@/partials/PopupEditer";
import { useViewport } from "@/helpers/useViewport";

export default {
  name: "MandatesTable",
  components: {
    Loading,
    NoData,
    PopupEditer
  },
  computed: {
    ...mapGetters(["mandates", "mandatesLoading", "customer", "mandateDeliveryStatuses"]),
    customerTeamId() {
      const teamId = Number(
        this.customer?.team_id
        ?? this.customer?.team?.id
        ?? this.$route?.params?.team_id
        ?? 0
      );

      return Number.isInteger(teamId) && teamId > 0 ? teamId : null;
    },
    canAccessMandates() {
      if (this.$store.getters.isSuperAdmin) return true;
      if (!this.customerTeamId) return false;

      return this.$store.getters.hasTeamPermission('mandate_access', this.customerTeamId);
    },
    canEditMandate() {
      if (this.$store.getters.isSuperAdmin) return true;
      if (!this.customerTeamId) return false;

      return this.$store.getters.hasTeamPermission('mandate_edit', this.customerTeamId);
    },
    calculatedDeliveriesHeights() {
      const heights = {};
      if (this.mandates && this.mandates.length > 0) {
        this.mandates.forEach(mandate => {
          if (this.hasRequiredSignatures(mandate) && this.hasCompanyDeliveries(mandate)) {
            const headerHeight = 37;
            const rowHeight = 80;
            const totalHeight = headerHeight + (mandate.companyDeliveries.length * rowHeight);
            heights[mandate.id] = totalHeight;
          }
        });
      }
      return heights;
    }
  },
  data() {
    return {
      isMobileView: useViewport(900),
      downloadingId: null,
      lastFetchedCustomerId: null,
      expandedDeliveries: {},
      showStatusPopup: false,
      editingDeliveryId: null,
      statuses: [],
      activeMandateId: null,
    };
  },
  watch: {
    '$route.params.id': {
      immediate: false,
      handler(newId, oldId) {
        if (newId && oldId && newId !== oldId && this.lastFetchedCustomerId !== newId) {
          this.$store.dispatch("fetchMandates", newId).then(() => {
            this.lastFetchedCustomerId = newId;
          });
        }
      }
    }
  },
  async mounted() {
    const customerId = this.$route.params.id;

    if (this.lastFetchedCustomerId === customerId) {
      return;
    }

    await this.$store.dispatch("fetchMandates", customerId);
    await this.$store.dispatch("getMandateDeliveryStatuses");
    this.lastFetchedCustomerId = customerId;
  },
  methods: {
    isManualMandate(mandate) {
      return mandate.customer_mandate_mode?.id === 2;
    },

    requiresAgentSignature(mandate) {
      return ![11, 12, 13, 14].includes(mandate.mandate_type?.id) && !this.isManualMandate(mandate);
    },

    hasRequiredSignatures(mandate) {
      return this.isManualMandate(mandate) || (mandate.is_signed &&
        (!this.requiresAgentSignature(mandate) || mandate.is_signed_by_agent));
    },

    formatDate(date) {
      if (!date) return '-----';
      const d = new Date(date);
      if (isNaN(d.getTime())) return date;
      const pad = (n) => String(n).padStart(2, '0');
      const day = pad(d.getDate());
      const month = pad(d.getMonth() + 1);
      const year = d.getFullYear();
      const hours = pad(d.getHours());
      const minutes = pad(d.getMinutes());
      return `${day}.${month}.${year} (${hours}:${minutes})`;
    },

    openManualMandateFiles(mandate) {
      this.$store.commit("setShowFilesEditModal", true);
      this.$store.commit("setFilesToEditId", mandate.id);
      this.$store.commit("setFilesToEdit", mandate.files);
      this.$store.commit("setFilesEditMode", 'Mandates');
    },


    async downloadMandate(mandateId) {
      this.downloadingId = mandateId;
      try {
        const customer = (this.customer && this.customer.id)
          ? this.customer
          : { id: this.$route.params.id };

        var name = null;

        if (customer.first_name && customer.last_name) {
          name = `${customer.first_name} ${customer.last_name}`;
        } else if ((!customer.first_name || !customer.last_name) && customer.company_name) {
          name = customer.company_name;
        } else {
          name = `customer ${customer.id}`;
        }

        await this.$store.dispatch('downloadMandatePDF', {
          customerId: customer.id,
          mandateId,
          name,
        });
      } finally {
        this.downloadingId = null;
      }
    },

    openMandateEmailModal(mandateId) {
      this.$store.commit("setMandateEmailModal", {
        display: true,
        mandateId,
      });
    },

    closeMandateDeletePopup(mandateId) {
      const deleteRef = this.$refs['delete-' + mandateId];
      if (deleteRef) {
        const target = Array.isArray(deleteRef) ? deleteRef[0] : deleteRef;
        if (target && target.blur) {
          target.blur();
        }
      }
      this.activeMandateId = null;
    },

    async deleteMandate(mandateId) {
      if (!this.canEditMandate) return;

      try {
        await this.$store.dispatch("deleteMandate", {
          customerId: this.$route.params.id,
          mandateId,
        });
        this.activeMandateId = null;
      } catch (error) {
        console.error("Failed to delete mandate:", error);
      }
    },

    toggleDeliveriesExpand(mandateId) {
      this.expandedDeliveries[mandateId] = !this.expandedDeliveries[mandateId];
    },

    openStatusPopup(deliveryId, status) {
      if (!this.canEditMandate) return;
      if (!status || status.code === "draft") return;

      this.editingDeliveryId = deliveryId;
      this.statuses = this.mandateDeliveryStatuses || [];
      this.showStatusPopup = true;
    },

    async updateStatus(newStatus) {
      if (!this.canEditMandate) return;
      if (this.editingDeliveryId && newStatus) {
        try {
          await this.$store.dispatch('updateDeliveryStatus', {
            deliveryId: this.editingDeliveryId,
            status: newStatus,
          });

          const customerId = this.$route.params.id;
          await this.$store.dispatch("fetchMandates", customerId);
        } finally {
          this.showStatusPopup = false;
          this.editingDeliveryId = null;
        }
      } else {
        this.showStatusPopup = false;
        this.editingDeliveryId = null;
      }
    },

    hasDraftDeliveries(mandate) {
      return mandate.companyDeliveries?.some(
        delivery => delivery.status_id === 10
      );
    },

    hasCompanyDeliveries(mandate) {
      return (mandate.companyDeliveries?.length ?? 0) > 0;
    },
  },
};
</script>

<style scoped lang="scss">
.centered-row {
  justify-content: center !important;
}

.mandate-actions,
.mandate-actions {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}

.icon__download {
  width: 24px;
  height: 24px;
  cursor: pointer;
  background: url("../../assets/icons/default/download_icon.svg") no-repeat center;
  background-size: contain;
  transition: opacity 0.2s ease;
}

.icon__download:hover:not(.icon--downloading) {
  opacity: 0.7;
}

.icon--downloading {
  opacity: 0.5;
  cursor: not-allowed;
}

.signing-status-messages {
  margin-top: 8px;
  padding: 0 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.status-message {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 13px;
}

.status-message--warning {
  background-color: #fff3cd;
  border: 1px solid #ffc107;
  color: #856404;
}

.status-icon {
  margin-right: 8px;
  font-size: 16px;
}

.status-text {
  flex: 1;
  color: var(--text-9);
}

/* Dark mode support */
@media (prefers-color-scheme: dark) {
  .status-message--warning {
    background-color: rgba(255, 193, 7, 0.08);
    border: 1px solid rgba(255, 193, 7, 0.28);
    color: #ffe8a1;
  }

  .status-icon {
    color: #ffd54f;
  }

  .signing-status-messages {
    background: transparent;
  }
}

/* Support explicit dark-theme classes */
.dark-mode .status-message--warning,
.theme-dark .status-message--warning {
  background-color: rgba(255, 193, 7, 0.08);
  border: 1px solid rgba(255, 193, 7, 0.28);
  color: #ffe8a1;
}

.dark-mode .status-icon,
.theme-dark .status-icon {
  color: #ffd54f;
}

.row__data--rotate {
  transform: rotate(180deg);
  transition: transform 0.3s ease-in-out;
}

.delivery-company {
  padding-left: 10px;
}

.data-card.policy-service {
  position: relative;
  overflow: hidden;
  padding-left: 16px;

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    width: 8px;
    height: 100%;
    background: transparent;
  }
}

.row__data--underline {
  color: var(--text-10) !important;
}

.row__data--clickable {
  cursor: pointer;
}

.policy-service__data {
  position: relative;
}

.policy-service__row {
  position: relative;

  &::before {
    position: absolute;
    z-index: 3;
    content: "";
    left: 0;
    top: -1px;
    height: calc(100% + 2px);
    width: 15px;
    border-top-left-radius: var(--radius-1);
    border-bottom-left-radius: var(--radius-1);
  }
}

.table .row__data--email {
  background: none;
  background-color: #999;
  width: 24px;
  height: 24px !important;

  mask: url("~@/assets/icons/default/mail_icon.svg") center / contain no-repeat;
  -webkit-mask: url("~@/assets/icons/default/mail_icon.svg") center / contain no-repeat;

  &.email--draft {
    background-color: #FF9E0F;
  }

  &.email--sent {
    background-color: #22db8e;
  }
}

.delivery--status--1 {

  .policy-service__row::before,
  &.data-card.policy-service::before {
    background-color: #FF9E0F;
  }
}

.delivery--status--2 {

  .policy-service__row::before,
  &.data-card.policy-service::before {
    background-color: #22db8e;
  }
}

.delivery--status--3 {

  .policy-service__row::before,
  &.data-card.policy-service::before {
    background-color: #ff3333;
  }
}

.delivery--status--10 {

  .policy-service__row::before,
  &.data-card.policy-service::before {
    background-color: #b1c4cf;
  }
}

:deep(.mandate-status-popup) {
  transform: translate(2%, 30px);
}


.loading {
  height: 76vh;
}

.delete {
  position: absolute;
  z-index: 3;
  background: var(--bg-1);
  right: calc(3% + 5px);
  top: 25%;
  transform: translateY(-50%);
  width: 300px;
  display: flex;
  align-items: center;
  border-radius: var(--radius-1);
  border: 1px solid var(--border-2);
  padding: 10px 20px;
  box-shadow: 0px 3px 6px var(--shadow-7);

  &__text {
    line-height: 20px;
    color: var(--text-9);
  }

  &__buttons {
    display: flex;
  }

  .button {
    width: 24px;
    height: 24px;
    cursor: pointer;
    transition: all 0.3s ease;

    &--confirm {
      background: url("../../assets/icons/default/confirm_icon.svg");
      margin: 0 15px;

      &:hover {
        background: url("../../assets/icons/default/confirm_hover_icon.svg");
      }
    }

    &--cancel {
      background: url("../../assets/icons/default/close_icon.svg");

      &:hover {
        background: url("../../assets/icons/default/close_hover_icon.svg");
      }
    }
  }
}

.actions-wrapper {
  padding: 0 !important;
  gap: 10px;

  .row__data {
    width: 24px;
    height: 24px;
    border: none !important;
  }
}

.data-record {
  .signing-status-messages {
    padding: 0 !important;
    width: 100% !important;
  }

  .policy__services .services {
    padding: 0 !important;
    margin-top: 25px;
    width: 100% !important;
    overflow: hidden;
    transition: max-height 0.25s ease;
    max-height: 0;

    &.services--expand {
      max-height: 100vh;
    }

    .policy-service {
      height: auto !important;
      max-height: initial !important;
    }
  }
}
</style>
